class VoiceService {
    constructor() {
        this.recognition = null;
        this.isListening = false;
        this.isSupported = false;
        this.onResult = null;
        this.onError = null;
        this.onStart = null;
        this.onEnd = null;
        this.triggerPhrase = 'hey chef';
        this.isTriggered = false;
        this.triggerTimeout = null;
        this.autoRestartEnabled = false;
        this.restartDelay = 250;
        this.restartTimeoutId = null;
        this.waitingForCommand = false;
        this.commandBuffer = '';
        this.bypassTrigger = false; // NEW: Allow continuous listening without trigger
        this.triggerVariants = [
            'hey chef',
            'hey chef',
            'hey chef'
        ];

        this.initializeRecognition();
    }

    initializeRecognition() {
        // Check if speech recognition is supported
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        
        if (!SpeechRecognition) {
            console.warn('Speech recognition not supported in this browser');
            this.isSupported = false;
            return;
        }

        this.isSupported = true;
        
        // Create speech recognition instance
        this.recognition = new SpeechRecognition();
        
        // Configure recognition settings
        this.recognition.continuous = true; // Keep listening
        this.recognition.interimResults = false; // Only final results
        this.recognition.lang = 'en-US';
        this.recognition.maxAlternatives = 1;
        
        // Set up event handlers
        this.recognition.onstart = () => {
            // console.log('Voice recognition started');
            this.isListening = true;
            this.onStart?.();
        };

        this.recognition.onresult = (event) => {
            for (let i = event.resultIndex; i < event.results.length; i += 1) {
                const result = event.results[i];
                if (!result.isFinal) {
                    continue;
                }

                const rawTranscript = (result[0]?.transcript || '').replace(/\s+/g, ' ').trim();

                if (!rawTranscript) {
                    continue;
                }

                // NEW: If bypass trigger is enabled, pass through immediately without checking for trigger
                if (this.bypassTrigger) {
                    // console.log('Voice recognition (continuous mode - no trigger required):', rawTranscript);
                    this.onResult?.(rawTranscript, false);
                    continue;
                }

                const lowerTranscript = rawTranscript.toLowerCase();
                const triggerMatch = this.findTriggerMatch(lowerTranscript);

                if (!this.isTriggered && triggerMatch) {
                    this.isTriggered = true;
                    this.waitingForCommand = false;
                    this.commandBuffer = '';

                    const commandText = this.stripTrigger(rawTranscript, triggerMatch.index, triggerMatch.length);

                    if (commandText) {
                        // console.log('Voice recognition command (with trigger):', commandText);
                        this.onResult?.(commandText, true);
                        this.resetTriggerState();
                    } else {
                        // console.log('Voice recognition trigger detected without command');
                        this.onResult?.('', true);
                        this.waitingForCommand = true;

                        clearTimeout(this.triggerTimeout);
                        this.triggerTimeout = setTimeout(() => {
                            // console.log('Voice trigger timeout - resetting trigger state');
                            this.resetTriggerState();
                        }, 10000); // 10 seconds to allow time for follow-up speech
                    }
                    continue;
                }

                if (this.isTriggered) {
                    const commandText = this.stripAllTriggers(rawTranscript);

                    if (!commandText) {
                        continue;
                    }

                    // console.log('Voice recognition command (post-trigger):', commandText);
                    this.onResult?.(commandText, false);
                    this.resetTriggerState();
                }
            }
        };

        this.recognition.onerror = (event) => {
            // Only log errors that are not expected/normal behavior
            if (event.error !== 'no-speech') {
                console.error('Voice recognition error:', event.error);
            }
            this.isListening = false;
            this.resetTriggerState();
            
            // Handle specific error types
            if (event.error === 'no-speech') {
                // No speech detected - this is normal, don't disable auto-restart
                // console.log('No speech detected - continuing to listen');
                // Don't disable auto-restart for no-speech errors
                // The onend handler will restart automatically
            } else if (event.error === 'audio-capture') {
                // Audio capture issues - don't restart
                // console.log('Audio capture error - disabling auto-restart');
                this.autoRestartEnabled = false;
            } else if (event.error === 'not-allowed') {
                // Permission denied - don't restart
                // console.log('Permission denied - disabling auto-restart');
                this.autoRestartEnabled = false;
            } else if (event.error === 'network') {
                // Network errors - don't restart
                // console.log('Network error - disabling auto-restart');
                this.autoRestartEnabled = false;
            }
            
            this.onError?.(event.error);
        };

        this.recognition.onend = () => {
            // console.log('Voice recognition ended');
            this.isListening = false;
            // Don't reset isTriggered if we're waiting for a command
            if (!this.waitingForCommand) {
                this.isTriggered = false;
            } else {
                // console.log('Keeping isTriggered=true while waiting for command');
            }
            this.onEnd?.();

            if (!this.autoRestartEnabled) {
                // console.log('Auto-restart disabled; staying stopped');
                return;
            }

            // Always restart when auto-restart is enabled (unless explicitly disabled)
            clearTimeout(this.restartTimeoutId);
            this.restartTimeoutId = setTimeout(() => {
                this.restartTimeoutId = null;

                if (!this.autoRestartEnabled || this.isListening || !this.isSupported || !this.recognition) {
                    return;
                }

                try {
                    // console.log('Restarting voice recognition...');
                    this.recognition.start();
                } catch (error) {
                    // console.log('Failed to restart voice recognition:', error);
                    // Only disable auto-restart for specific errors, not all failures
                    if (error?.name === 'InvalidStateError') {
                        // Try again after a longer delay for InvalidStateError
                        setTimeout(() => {
                            if (this.autoRestartEnabled && !this.isListening) {
                                try {
                                    this.recognition.start();
                                } catch (retryError) {
                                    // console.log('Retry restart failed:', retryError);
                                }
                            }
                        }, this.restartDelay * 2);
                    }
                }
            }, this.restartDelay);
        };
    }

    // Start listening for voice input
    startListening() {
        // Try to reinitialize if recognition failed
        if (!this.recognition && this.isSupported) {
            // console.log('Reinitializing voice recognition...');
            this.initializeRecognition();
        }

        if (!this.isSupported || !this.recognition) {
            console.warn('Voice recognition not available');
            this.onError?.('Voice recognition not supported in this browser');
            return false;
        }

        if (this.isListening) {
            // console.log('Already listening');
            return true;
        }

        // Re-enable auto-restart when manually starting
        this.autoRestartEnabled = true;
        clearTimeout(this.restartTimeoutId);
        this.restartTimeoutId = null;

        try {
            // console.log('Starting voice recognition...');
            this.recognition.start();
            return true;
        } catch (error) {
            console.error('Failed to start voice recognition:', error);
            if (error?.name === 'InvalidStateError') {
                // Recognition might still be running, try to stop it first
                try {
                    this.recognition.stop();
                } catch (stopError) {
                    // Ignore stop errors
                }
                
                // Wait a bit longer before retrying for InvalidStateError
                clearTimeout(this.restartTimeoutId);
                this.restartTimeoutId = setTimeout(() => {
                    this.restartTimeoutId = null;
                    if (!this.autoRestartEnabled || this.isListening || !this.recognition) {
                        return;
                    }
                    try {
                        // console.log('Retrying voice recognition start...');
                        this.recognition.start();
                    } catch (retryError) {
                        console.error('Retry to start voice recognition failed:', retryError);
                        // Disable auto-restart if retry fails
                        this.autoRestartEnabled = false;
                    }
                }, this.restartDelay * 2); // Wait longer for retry
            } else {
                this.onError?.(error);
            }
            return false;
        }
    }

    // Stop listening
    stopListening({ disableAutoRestart = true } = {}) {
        if (disableAutoRestart) {
            this.autoRestartEnabled = false;
        }

        clearTimeout(this.restartTimeoutId);
        this.restartTimeoutId = null;

        if (this.recognition) {
            try {
                this.recognition.stop();
            } catch (error) {
                if (error?.name !== 'InvalidStateError') {
                    console.warn('Failed to stop voice recognition gracefully:', error);
                }
                try {
                    this.recognition.abort();
                } catch (abortError) {
                    if (abortError?.name !== 'InvalidStateError') {
                        console.warn('Failed to abort voice recognition:', abortError);
                    }
                }
            }
        }

        this.isListening = false;
        this.isTriggered = false;
        this.waitingForCommand = false;
        this.commandBuffer = '';
        clearTimeout(this.triggerTimeout);
        this.triggerTimeout = null;
    }

    // Set event handlers
    setOnResult(callback) {
        this.onResult = callback;
    }

    setOnError(callback) {
        this.onError = callback;
    }

    setOnStart(callback) {
        this.onStart = callback;
    }

    setOnEnd(callback) {
        this.onEnd = callback;
    }

    // Check if currently listening
    getListeningState() {
        return {
            isListening: this.isListening,
            isTriggered: this.isTriggered,
            isSupported: this.isSupported,
            autoRestartEnabled: this.autoRestartEnabled
        };
    }

    // Enable auto-restart (useful after errors)
    enableAutoRestart() {
        this.autoRestartEnabled = true;
        // console.log('Auto-restart enabled');
    }

    // Disable auto-restart
    disableAutoRestart() {
        this.autoRestartEnabled = false;
        // console.log('Auto-restart disabled');
    }

    // Explicitly disable voice recognition (user action)
    disableVoiceRecognition() {
        // console.log('Voice recognition explicitly disabled by user');
        this.autoRestartEnabled = false;
        this.stopListening({ disableAutoRestart: true });
    }

    // Check if voice recognition should be active
    shouldBeListening() {
        return this.autoRestartEnabled && this.isSupported;
    }

    // Update trigger phrase dynamically
    updateTriggerPhrase(newTriggerPhrase) {
        this.triggerPhrase = newTriggerPhrase.toLowerCase();
        this.triggerVariants = [
            this.triggerPhrase,
            this.triggerPhrase,
            this.triggerPhrase
        ];
        // console.log('Trigger phrase updated to:', this.triggerPhrase);
    }

    // Check browser compatibility
    checkBrowserSupport() {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
        const isEdge = /Edg/.test(navigator.userAgent);
        const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);
        
        return {
            hasSpeechRecognition: !!SpeechRecognition,
            isChrome,
            isEdge,
            isSafari,
            userAgent: navigator.userAgent
        };
    }

    // Update trigger phrase
    setTriggerPhrase(phrase) {
        this.triggerPhrase = phrase.toLowerCase().trim();
    }

    /**
     * Enable/disable bypass trigger mode for continuous listening
     */
    setBypassTrigger(bypass) {
        // console.log(`🎤 Bypass trigger mode: ${bypass ? 'ENABLED' : 'DISABLED'}`);
        this.bypassTrigger = bypass;
        // Reset trigger state when changing modes
        if (!bypass) {
            this.resetTriggerState();
        }
    }

    // Clean up
    destroy() {
        // console.log('🗑️ Destroying voice service...');
        this.stopListening();
        
        // Force stop recognition if it still exists
        if (this.recognition) {
            try {
                this.recognition.stop();
            } catch (error) {
                // console.log('Voice recognition already stopped');
            }
            try {
                this.recognition.abort();
            } catch (error) {
                // console.log('Voice recognition already aborted');
            }
            this.recognition = null;
        }
        
        // Clear all callbacks
        this.onResult = null;
        this.onError = null;
        this.onStart = null;
        this.onEnd = null;
        
        // Clear timeouts
        clearTimeout(this.triggerTimeout);
        this.triggerTimeout = null;
        clearTimeout(this.restartTimeoutId);
        this.restartTimeoutId = null;
        
        // Reset state
        this.autoRestartEnabled = false;
        this.waitingForCommand = false;
        this.commandBuffer = '';
        this.isTriggered = false;
        this.isListening = false;
        
        // console.log('✅ Voice service destroyed');
    }

    findTriggerMatch(transcriptLower) {
        let bestMatch = null;
        for (const variant of this.triggerVariants) {
            const index = transcriptLower.lastIndexOf(variant);
            if (index >= 0) {
                if (!bestMatch || index > bestMatch.index) {
                    bestMatch = { index, length: variant.length };
                }
            }
        }
        return bestMatch;
    }

    stripTrigger(transcript, startIndex, triggerLength) {
        return transcript.substring(startIndex + triggerLength).trim();
    }

    stripAllTriggers(transcript) {
        let cleaned = transcript;
        for (const variant of this.triggerVariants) {
            const escaped = variant.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
            cleaned = cleaned.replace(new RegExp(escaped, 'gi'), ' ');
        }
        return cleaned.replace(/\s+/g, ' ').trim();
    }

    resetTriggerState() {
        this.isTriggered = false;
        this.waitingForCommand = false;
        this.commandBuffer = '';
        clearTimeout(this.triggerTimeout);
        this.triggerTimeout = null;
    }
}

// Create singleton instance
const voiceService = new VoiceService();

// Global cleanup function for navigation
export const cleanupVoiceService = () => {
    // console.log('🧹 Global voice service cleanup triggered');
    voiceService.destroy();
};

// Add global cleanup on page unload
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', cleanupVoiceService);
    window.addEventListener('pagehide', cleanupVoiceService);
}

export default voiceService;
